import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MJAuthBase } from '@memberjunction/ng-auth-services';
import { AGUScrollportHolder, SyncSurfaceImmersiveClass } from 'agu_app_shell';
import { MapsHostAdapter, MapsLocation, MapsView } from 'maps_ui';
import { environment } from '../../environments/environment';

/**
 * Body class that puts MJExplorer into "immersive MAPS" mode — the global
 * stylesheet (apps/MJExplorer/src/styles.scss) hangs the shell-chrome-hiding
 * rules off it. It is DERIVED state — set exclusively by SyncImmersiveBodyClass
 * below; the adapter additionally drops it eagerly on sign-out/return so the
 * shell chrome reappears without waiting for the next recompute signal.
 */
export const IMMERSIVE_BODY_CLASS = 'maps-immersive';

/** Element selector the immersive predicate measures for this surface. */
const MAPS_SURFACE_SELECTOR = 'mj-maps-surface';

/**
 * Single source of truth for MAPS immersive mode: the class is ON exactly when
 * some connected, non-zero-size MAPS surface is in the document.
 *
 * The predicate itself lives in packages/AGUAppShell, because every AGU surface
 * needs the identical derived-state behavior and the rules encoded there
 * (detach-without-destroy, duplicate instances, late destroys, SetVisible never
 * firing) already cost one production bug to get right — N copies would have to
 * be fixed N times. This wrapper keeps the one-argument signature every MAPS
 * call site uses.
 */
export function SyncImmersiveBodyClass(doc: Document): void {
    SyncSurfaceImmersiveClass(doc, MAPS_SURFACE_SELECTOR, IMMERSIVE_BODY_CLASS);
}

/**
 * MapsHostAdapter for the MJExplorer-hosted surface: the dashboard has no URL
 * routes, so "navigation" is a BehaviorSubject the surface template switches
 * on. Sign-out is MJ sign-out — magic-link guests and staff alike leave via
 * MJAuthBase (the magic-link provider clears its per-tab token in logout()).
 */
@Injectable()
export class ExplorerMapsHostAdapter implements MapsHostAdapter {
    private readonly location = new BehaviorSubject<MapsLocation>('search');

    constructor(
        private readonly auth: MJAuthBase,
        private readonly router: Router,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        // Belt-and-braces re-sync on every route change: tab activations are
        // exactly when the shell attaches/detaches surfaces, and a detached
        // surface (hidden tab) stays alive with its adapter — so this signal
        // still fires in windows where no component-level event does. The
        // setTimeout lets the shell finish moving DOM before we measure it.
        // Ends with the surface that provides this adapter (its destroy
        // re-syncs the class one last time itself).
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd), takeUntilDestroyed()).subscribe(() => {
            setTimeout(() => SyncImmersiveBodyClass(this.document));
        });
    }

    /**
     * MAPS API origin for magic-link redeem URLs, derived from the same
     * GRAPHQL_URI the Apollo client uses (see maps-surface.module.ts) — the
     * API host serves /magic-link/redeem, not the SPA host.
     */
    get ApiOrigin(): string {
        const base = (environment as { GRAPHQL_URI?: string }).GRAPHQL_URI || 'http://localhost:4000/';
        return base.replace(/\/graphql\/?$/, '').replace(/\/+$/, '');
    }

    /**
     * Recompute MAPS immersive mode. Satisfies AGUImmersiveSurfaceHost so
     * BaseAGUAppSurfaceComponent can drive this surface's lifecycle without
     * knowing anything about MAPS.
     */
    SyncImmersiveClass(): void {
        SyncImmersiveBodyClass(this.document);
    }

    /** The surface's scrollport, registered by BaseAGUAppSurfaceComponent on init (AGUImmersiveSurfaceHost). */
    private readonly scrollport = new AGUScrollportHolder();

    AttachScrollport(element: HTMLElement): void {
        this.scrollport.Attach(element);
    }

    DetachScrollport(element: HTMLElement): void {
        this.scrollport.Detach(element);
    }

    get ScrollTop(): number {
        return this.scrollport.ScrollTop;
    }

    set ScrollTop(value: number) {
        this.scrollport.ScrollTop = value;
    }

    get Location$(): Observable<MapsLocation> {
        return this.location.asObservable();
    }

    get CurrentLocation(): MapsLocation {
        return this.location.value;
    }

    /**
     * Staff (SSO) sessions get a "Back to Explorer" affordance — immersive mode
     * hides the whole shell, and sign-out would otherwise be their only exit.
     * Magic-link guests never see it: their session is scoped to the MAPS app
     * and the shell has nothing else to offer them.
     */
    get CanReturnToHost(): boolean {
        return this.auth.type !== 'magic-link';
    }

    ReturnToHost(): void {
        this.document.body.classList.remove(IMMERSIVE_BODY_CLASS);
        // '/' would re-show the last active workspace tab — which is MAPS, so
        // the click would appear to do nothing but add a header. Land on the
        // Explorer Home app (the app grid) instead; the MAPS tab stays open in
        // the tab strip for a one-click return.
        void this.router.navigateByUrl('/app/home');
    }

    NavigateTo(view: MapsView): void {
        this.location.next(view);
    }

    async SignOut(): Promise<void> {
        this.document.body.classList.remove(IMMERSIVE_BODY_CLASS);
        const wasMagicLink = this.auth.type === 'magic-link';
        try {
            await this.auth.logout();
        } catch {
            // logout() clears client caches before the provider redirect; if the
            // provider step throws we've still torn down local state — nothing
            // useful to surface to the user here.
        }
        if (wasMagicLink) {
            // The auth provider is selected once at app boot (the factory picks
            // the magic-link provider whenever a session token is present).
            // After a magic-link sign-out this tab would keep that provider,
            // whose login() is a no-op — leaving the login screen's "Log In"
            // button dead until a manual reload. Hard-reload so the app reboots
            // onto the SSO provider and login works immediately. SSO logouts
            // don't need this: the provider itself performs a full redirect.
            this.document.defaultView?.location.assign('/');
        }
    }
}
